import React, { useMemo, useRef, useState } from 'react';
import CatalogImage from '../catalog/CatalogImage';
import ProductPrice from '../catalog/ProductPrice';
import { useAuth } from '../auth/AuthContext';
import { useProductViewImpression } from '../hooks/useProductViewImpression';
import { PRODUCT_VIEW_SOURCES } from '../storefront/productViewSources';
import { useProducts } from '../useProducts';
import { coerceCatalogVisible, sortProductsByOrder } from '../catalogNormalize';
import { productGalleryImagePaths } from '../productMediaUtils';

const ALL_SUB = '__all__';

function normKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');
}

function productVisible(product) {
  return coerceCatalogVisible(product?.is_visible);
}

function productMatchesSubcategory(product, subSlug) {
  if (!subSlug || subSlug === ALL_SUB) return true;
  const keys = new Set([normKey(product?.subcategory), normKey(product?.type)]);
  return keys.has(normKey(subSlug));
}

function chipClass(active) {
  return `exhibition-preorder-picker__chip${active ? ' exhibition-preorder-picker__chip--active' : ''}`;
}

function ExhibitionPreorderProductRow({ product, categorySlug, qty, onSetQty }) {
  const rowRef = useRef(null);
  const { token } = useAuth();
  const id = String(product.id || '').trim();
  useProductViewImpression({
    enabled: !!categorySlug && !!id,
    categorySlug,
    productId: id,
    productName: product.name || id,
    viewSource: PRODUCT_VIEW_SOURCES.EXHIBITION,
    authToken: token || null,
    rootRef: rowRef,
  });

  return (
    <div ref={rowRef} className="exhibition-preorder-picker__product" role="listitem">
      <CatalogImage
        src={productGalleryImagePaths(product)[0]}
        alt=""
        className="exhibition-preorder-picker__thumb"
      />
      <div className="exhibition-preorder-picker__meta">
        <div className="exhibition-preorder-picker__name">{product.name || id}</div>
        <ProductPrice product={product} className="exhibition-preorder-picker__price" />
      </div>
      <div className="exhibition-preorder-picker__qty" aria-label={`Quantity for ${product.name || id}`}>
        <button
          type="button"
          className="exhibition-preorder-picker__qty-btn"
          onClick={() => onSetQty(product, qty - 1)}
          disabled={qty <= 0}
          aria-label="Decrease quantity"
        >
          −
        </button>
        <span className="exhibition-preorder-picker__qty-val">{qty}</span>
        <button
          type="button"
          className="exhibition-preorder-picker__qty-btn"
          onClick={() => onSetQty(product, qty + 1)}
          aria-label="Increase quantity"
        >
          +
        </button>
      </div>
    </div>
  );
}

export default function ExhibitionPreorderPicker({ selections, onChange, extraNote, onExtraNoteChange }) {
  const { categories, products, loading } = useProducts();
  const [categorySlug, setCategorySlug] = useState('');
  const [subSlug, setSubSlug] = useState(ALL_SUB);

  const visibleCategories = useMemo(() => {
    return (categories || []).filter((cat) => {
      if (!coerceCatalogVisible(cat?.is_visible)) return false;
      const list = products?.[cat.slug] || [];
      return list.some(productVisible);
    });
  }, [categories, products]);

  const activeCategory = useMemo(() => {
    if (visibleCategories.length === 0) return null;
    const found = visibleCategories.find((c) => c.slug === categorySlug);
    return found || visibleCategories[0];
  }, [visibleCategories, categorySlug]);

  const subcategories = useMemo(() => {
    if (!activeCategory) return [];
    const fromTaxonomy = (activeCategory.subcategories || []).filter((s) => coerceCatalogVisible(s?.is_visible));
    const list = sortProductsByOrder(products?.[activeCategory.slug] || []).filter(productVisible);
    if (fromTaxonomy.length > 0) return fromTaxonomy;
    const seen = new Map();
    list.forEach((p) => {
      const label = String(p.subcategory || p.type || '').trim();
      if (!label) return;
      const slug = normKey(label);
      if (!seen.has(slug)) seen.set(slug, label);
    });
    return [...seen.entries()].map(([slug, name], i) => ({ slug, name, sort_order: i }));
  }, [activeCategory, products]);

  const filteredProducts = useMemo(() => {
    if (!activeCategory) return [];
    return sortProductsByOrder(products?.[activeCategory.slug] || [])
      .filter(productVisible)
      .filter((p) => productMatchesSubcategory(p, subSlug));
  }, [activeCategory, products, subSlug]);

  const selectedList = useMemo(
    () => Object.values(selections || {}).filter((item) => Number(item?.qty) > 0),
    [selections],
  );

  const totalQty = selectedList.reduce((sum, item) => sum + Number(item.qty || 0), 0);

  const setQty = (product, qty) => {
    const id = String(product.id || '').trim();
    if (!id) return;
    const next = Math.max(0, Math.min(99, Number(qty) || 0));
    const map = { ...(selections || {}) };
    if (next <= 0) {
      delete map[id];
    } else {
      const price = Number(product.price || 0);
      const rawSale =
        product.sale_price != null && product.sale_price !== '' ? Number(product.sale_price) : null;
      const unitPrice =
        rawSale != null && Number.isFinite(rawSale) && rawSale > 0 && rawSale < price ? rawSale : price;
      map[id] = {
        id,
        name: String(product.name || '').trim() || id,
        category: activeCategory?.slug || product.category || '',
        subcategory: String(product.subcategory || product.type || '').trim(),
        qty: next,
        image: productGalleryImagePaths(product)[0] || '',
        unit_price: unitPrice,
      };
    }
    onChange?.(map);
  };

  if (loading) {
    return <p className="small text-muted mb-0">Loading shop catalog…</p>;
  }

  if (visibleCategories.length === 0) {
    return (
      <p className="small text-muted mb-0">
        Catalog is not available right now — describe what you want in the notes below.
      </p>
    );
  }

  return (
    <div className="exhibition-preorder-picker">
      {totalQty > 0 ? (
        <div className="exhibition-preorder-picker__summary" aria-live="polite">
          <span className="exhibition-preorder-picker__summary-label">Your picks</span>
          <span className="exhibition-preorder-picker__summary-count">
            {totalQty} item{totalQty === 1 ? '' : 's'}
          </span>
          <div className="exhibition-preorder-picker__summary-chips">
            {selectedList.map((item) => (
              <span key={item.id} className="exhibition-preorder-picker__summary-chip">
                {item.qty}× {item.name}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="exhibition-preorder-picker__filters">
        <div>
          <span className="exhibition-preorder-picker__filter-label">Category</span>
          <div className="exhibition-preorder-picker__chips">
            {visibleCategories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                className={chipClass(activeCategory?.slug === cat.slug)}
                onClick={() => {
                  setCategorySlug(cat.slug);
                  setSubSlug(ALL_SUB);
                }}
              >
                {cat.name || cat.slug}
              </button>
            ))}
          </div>
        </div>

        {subcategories.length > 0 ? (
          <div>
            <span className="exhibition-preorder-picker__filter-label">Collection</span>
            <div className="exhibition-preorder-picker__chips">
              <button
                type="button"
                className={chipClass(subSlug === ALL_SUB)}
                onClick={() => setSubSlug(ALL_SUB)}
              >
                All
              </button>
              {subcategories.map((sub) => (
                <button
                  key={sub.slug}
                  type="button"
                  className={chipClass(subSlug === sub.slug)}
                  onClick={() => setSubSlug(sub.slug)}
                >
                  {sub.name || sub.slug}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="exhibition-preorder-picker__products" role="list">
        {filteredProducts.length === 0 ? (
          <p className="small text-muted mb-0 px-1">No pieces in this collection.</p>
        ) : (
          filteredProducts.map((product) => {
            const id = String(product.id || '').trim();
            const qty = Number(selections?.[id]?.qty || 0);
            return (
              <ExhibitionPreorderProductRow
                key={id}
                product={product}
                categorySlug={activeCategory?.slug || ''}
                qty={qty}
                onSetQty={setQty}
              />
            );
          })
        )}
      </div>

      <div className="exhibition-preorder-picker__extra">
        <label className="exhibition-rsvp__label" htmlFor="exhibition-preorder-extra">
          Anything else? <span className="exhibition-rsvp__hint-inline">(optional)</span>
        </label>
        <textarea
          id="exhibition-preorder-extra"
          className="exhibition-rsvp__input exhibition-rsvp__input--textarea"
          rows={2}
          value={extraNote}
          onChange={(e) => onExtraNoteChange?.(e.target.value)}
          placeholder="Custom notes, sizes, or pieces not listed above"
          maxLength={1000}
        />
      </div>
    </div>
  );
}
